import type { Option } from "@tento-lona/sheets";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import type { CellCardinality } from "@tento-lona/sheets/platform";
import { type CellEntry, type CellId, type Json, RowId, type RowNetworkKey, type SheetNetworkKey, type SyncKey, UserId } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import type { RowData } from "../sync/row-data.ts";
import { TaggedCells } from "./tagged-cells.ts";

/**
 * The install transaction of ONE bulk cell response (`CellAccessor`'s sheet
 * fetch), row by row, after the response's lease, ingestion gate and install
 * pacer admitted it: store each row's metadata, resolve the cache the row
 * routes to now, install the cells against the revision the lease captured,
 * hand the observer the same fresh cells, apply sync keys — and keep the
 * sets the caller finishes with (`seen`, `staleIds`, `coveredIds`, `retryRows`).
 *
 * A row whose metadata storage re-routed or replaced its cache, or whose
 * cache this response's lease does not own, is not installed: it is left in
 * `retryRows` for a fresh read. `installRow` answers `false` once the sheet
 * stopped wanting the answer (`BulkInstall.Port.bail`): the caller abandons
 * the rest of the response, its formulas and its notification.
 */
export class BulkInstall {
  /** Rows the response carried (by the visible key they installed under). */
  readonly seen = new Set<RowNetworkKey>();
  /** Rows whose own copy changed: the caller evaluates formulas and notifies them. */
  readonly staleIds = new Set<RowNetworkKey>();
  /** Rows whose days the response first covered with no cells: the caller announces the coverage alone. */
  readonly coveredIds = new Set<RowNetworkKey>();
  /** Rows the caller re-reads: their cache was not this response's to install into. */
  readonly retryRows = new Set<RowNetworkKey>();
  readonly #port: BulkInstall.Port;
  readonly #lease: BulkInstall.LeaseRead;
  readonly #observation: Option<BulkInstall.Observation>;
  readonly #utcRange: DateTime.Range<Utc>;
  readonly #naiveRange: NaiveDate.Range;

  constructor(
    port: BulkInstall.Port,
    lease: BulkInstall.LeaseRead,
    observation: Option<BulkInstall.Observation>,
    utcRange: DateTime.Range<Utc>,
    naiveRange: NaiveDate.Range,
  ) {
    this.#port = port;
    this.#lease = lease;
    this.#observation = observation;
    this.#utcRange = utcRange;
    this.#naiveRange = naiveRange;
  }

  /**
   * Prepare every declaration before committing any response metadata.
   * Defaults use the same owning registry as rows-side hydration; an explicit
   * instance field still wins, including a JSON null.
   */
  prepare(results: readonly Network.SheetRowDataResponse[]): BulkInstall.PreparedRow[] {
    const port = this.#port;
    return results.map((rowData) => {
      const rowId = port.visibleRowIdOf(rowData);
      const rh = port.rowHandle(rowId);
      const rawMeta = rowData.rowType ? Network.SheetRowDb.make({
        id: rowData.id,
        type: rowData.rowType,
        lookupKey: rowData.lookupKey,
        label: rowData.label,
        metadata: rowData.metadata,
        ownerId: UserId.DETACHED,
        timeScale: "week",
        attributes: rh.attributes,
        formula: null,
        heightPx: null,
        icon: null,
      }) : null;
      const metadata = rawMeta == null ? null : port.effectiveMetadata(rawMeta, rowId) ?? rawMeta;
      return { rowData, rowId, metadata };
    });
  }

  /** Install one prepared row; `false` when the sheet bailed (abandon the response). */
  installRow({ rowData, rowId, metadata }: BulkInstall.PreparedRow): boolean {
    const port = this.#port;
    if (port.bail()) return false;
    this.seen.add(rowId);

    if (metadata != null) {
      port.storeRowMeta(rowData.id, metadata);
      if (port.bail()) return false;
      if (rowId !== rowData.id.toString()) {
        port.storeRowMeta(RowId.of(rowId), { ...metadata, id: RowId.of(rowId) });
      }
    }

    if (port.bail()) return false;
    // Metadata storage can re-enter and replace a routed cache. Resolve
    // the actual target afterwards and require this response's own lease.
    const rh = port.rowHandle(rowId);
    const accessKey = port.accessKeyOf(rowId);
    const since = this.#lease.sinceFor(rowId, rh);
    if (since == null) {
      this.retryRows.add(rowId);
      return true;
    }
    const view = rh.view;
    if (metadata != null) rh.applyMeta(metadata, port.typeCardinality(metadata.type));
    if (port.bail()) return false;
    if (
      port.accessKeyOf(rowId) !== accessKey ||
      port.heldAt(accessKey) !== rh ||
      rh.view !== view ||
      this.#lease.sinceFor(rowId, rh) == null
    ) {
      this.retryRows.add(rowId);
      return true;
    }
    // Coverage and authoritative store ingest settle as one listener
    // fire inside the owner's response-install pacing.
    const release = rh.deferNotifications();
    let receipt: RowCellCache.Receipt = null;
    try {
      const observation = this.#observation;
      receipt = rh.view?.clientScoped && observation != null
        ? rh.receiveBatchCoverageSince(this.#utcRange, rowData.data, since)
        : rh.receiveBatchDataSince(this.#utcRange, rowData.data, since);
      if (cellPersistenceDebugBulkResult(rowData)) {
        Tracing.log("cell-persist/read/legacy-bulk", {
          sheetId: port.networkKey,
          rowId,
          serverRowId: rowData.id.toString(),
          lookupKey: rowData.lookupKey ?? null,
          label: rowData.label ?? null,
          rowType: rowData.rowType ?? null,
          changed: receipt,
          cells: cellPersistenceWireCellSummaries(rowData.data),
          rowHandleCells: cellPersistenceCellEntrySummaries(
            TaggedCells.dates(rh.cellsIn(this.#naiveRange)),
          ),
        });
      }
      const observe = observation?.receive ?? port.observer();
      observe?.({
        networkKey: port.networkKey,
        rowId,
        serverRowId: rowData.id,
        lookupKey: rowData.lookupKey ?? null,
        rowType: rowData.rowType ?? null,
        label: rowData.label ?? null,
        metadata: rowData.metadata ?? null,
        utcRange: this.#utcRange,
        // Apply the same freshness boundary before releasing the view.
        cells: rh.decodeCells(rh.filterBatchDataSince(rowData.data, since)),
      });
    } finally {
      release();
    }

    // Apply sync if syncKeys present
    if (rowData.syncKeys && rowData.syncKeys.length > 0) {
      port.applySync(rh, rowId, rowData.syncKeys, this.#utcRange);
    }

    if (receipt === "changed") this.staleIds.add(rowId);
    else if (receipt === "covered") this.coveredIds.add(rowId);
    return true;
  }
}

export namespace BulkInstall {
  /** What a bulk install needs from the accessor whose fetch it answers. */
  export interface Port {
    readonly networkKey: SheetNetworkKey;
    /** The sheet no longer wants the answer (disposed, or switched away: it re-reads on reactivation). */
    bail(): boolean;
    /** The visible key a response row installs under (a lookup-keyed row's shared access key). */
    visibleRowIdOf(rowData: Network.SheetRowDataResponse): RowNetworkKey;
    /** The row's cache under `rowId`, created on demand. */
    rowHandle(rowId: RowNetworkKey): RowCellCache;
    /** The access key `rowId` routes to now. */
    accessKeyOf(rowId: RowNetworkKey): RowNetworkKey;
    /** The cache held under `accessKey` now (`null`: none). */
    heldAt(accessKey: RowNetworkKey): Option<RowCellCache>;
    /** The sheet's effective metadata for a response row (`null`: the response's own). */
    effectiveMetadata(raw: Network.SheetRowDb, rowId: RowNetworkKey): Option<Network.SheetRowDb>;
    /** Store a row's metadata (detached from any sheet row). */
    storeRowMeta(id: RowId, metadata: Network.SheetRowDb): void;
    /** A row type's cell cardinality (`RowType`), applied with a row's metadata. */
    typeCardinality(type: string): CellCardinality;
    /** Apply a row's sync keys over `utcRange` and route its sync-state changes. */
    applySync(rh: RowCellCache, rowId: RowNetworkKey, syncKeys: readonly SyncKey[], utcRange: DateTime.Range<Utc>): void;
    /** The accessor's standing bulk observer, when no observation was captured for this response. */
    observer(): Option<FetchObserver>;
  }

  /** The revision a response's lease captured on `rh` (`null`: not this response's to install into). */
  export interface LeaseRead {
    sinceFor(rowId: RowNetworkKey, rh: RowCellCache): Option<number>;
  }

  /** One response row with the key it installs under and its prepared metadata. */
  export type PreparedRow = {
    readonly rowData: Network.SheetRowDataResponse;
    readonly rowId: RowNetworkKey;
    readonly metadata: Option<Network.SheetRowDb>;
  };

  export type FetchedRow = {
    readonly networkKey: SheetNetworkKey;
    readonly rowId: RowNetworkKey;
    readonly serverRowId: RowId;
    readonly lookupKey: Option<string>;
    readonly rowType: Option<string>;
    readonly label: Option<string>;
    readonly metadata: Option<Json>;
    readonly utcRange: DateTime.Range<Utc>;
    /**
     * Cells still eligible after the handle's response-time freshness check,
     * decoded as the handle's store holds them (`data` is the canonical value).
     */
    readonly cells: readonly RowData.Cell[];
  };

  export type FetchObserver = (row: FetchedRow) => void;

  /** Response-time ownership for a second store populated by the observer. */
  export interface Observation {
    readonly receive: FetchObserver;
    release(): void;
  }
}

function cellPersistenceDebugBulkResult(
  rowData: Network.SheetRowDataResponse,
): boolean {
  return rowData.lookupKey === ":in-progress" ||
    rowData.label === "In Progress";
}

/** A logged value's shape: its kind, its length (strings) and a short preview. */
type ValueSummary = {
  readonly kind: string;
  readonly len: Option<number>;
  readonly preview: Option<string>;
};

/** A logged cell: its date, its id and its value's shape. */
type CellSummary = {
  readonly date: string;
  readonly cellId: Option<CellId>;
  readonly value: ValueSummary;
};

function cellPersistenceWireCellSummaries(
  cells: readonly Network.SheetCell[],
): readonly CellSummary[] {
  const out: CellSummary[] = [];
  for (const cell of cells.slice(0, 6)) {
    out.push({
      date: cell.date.toString(),
      cellId: cell.id ?? null,
      value: cellPersistenceValueSummary(cell.data ?? null),
    });
  }
  return out;
}

function cellPersistenceCellEntrySummaries(
  cells: readonly CellEntry[],
): readonly CellSummary[] {
  const out: CellSummary[] = [];
  for (const cell of cells.slice(0, 6)) {
    out.push({
      date: cell.date.toString(),
      cellId: cell.id ?? null,
      value: cellPersistenceValueSummary(cell.value),
    });
  }
  return out;
}

function cellPersistenceValueSummary(value: unknown): ValueSummary {
  if (typeof value === "string") {
    return {
      kind: "string",
      len: value.length,
      preview: value.slice(0, 48),
    };
  }
  if (typeof value === "number") {
    return { kind: "number", len: null, preview: String(value) };
  }
  if (typeof value === "boolean") {
    return { kind: "boolean", len: null, preview: String(value) };
  }
  if (value === null) return { kind: "null", len: null, preview: null };
  return {
    kind: Array.isArray(value) ? "array" : "object",
    len: null,
    preview: null,
  };
}
